import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import toast from 'react-hot-toast';
import { customerApi } from '../../../api/customerApi';

/**
 * Confirmation d'inscription envoyée depuis la fiche apprenant.
 *
 * Deux étapes : on choisit les inscriptions à confirmer, puis on relit le ou
 * les mails avant de les envoyer. Les inscriptions sont regroupées par format,
 * car un mail présentiel parle de lieu et un mail classe virtuelle de lien de
 * connexion : seuls des formats identiques peuvent tenir dans un même message.
 */
const CustomerInscriptionConfirmationDialog = ({ open, onClose, customerId, onSent }) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const token = localStorage.getItem('accessToken');

  const loadOptions = useCallback(async () => {
    setLoading(true);
    setPreview(null);
    try {
      const res = await customerApi.getInscriptionConfirmationOptions(customerId, token);
      const rows = (res && res.data) || [];
      setOptions(rows);
      // Les formats sans modèle ne sont pas cochés : ils ne peuvent pas partir.
      setSelected(rows.filter((r) => r.family).map((r) => r.ls_id));
    } catch (err) {
      console.error(err);
      toast.error('Impossible de charger les inscriptions.');
    } finally {
      setLoading(false);
    }
  }, [customerId, token]);

  useEffect(() => {
    if (open) {
      loadOptions();
    }
  }, [open, loadOptions]);

  const toggle = (lsId) => {
    setPreview(null);
    setSelected((prev) => (prev.includes(lsId) ? prev.filter((id) => id !== lsId) : [...prev, lsId]));
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const res = await customerApi.previewInscriptionConfirmation(customerId, selected, token);
      if (res && res.status === 200) {
        setPreview(res.data || []);
      } else {
        toast.error((res && res.message) || 'Aperçu impossible.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Aperçu impossible.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await customerApi.sendInscriptionConfirmation(customerId, selected, token);
      if (res && res.status === 200) {
        toast.success(res.message || 'Confirmation envoyée.');
        if (onSent) onSent();
        onClose();
      } else {
        toast.error((res && res.message) || "L'envoi a échoué.");
      }
    } catch (err) {
      console.error(err);
      toast.error("L'envoi a échoué.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
      <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
        <MarkEmailReadIcon color="primary" />
        Confirmation d&apos;inscription
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : options.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: '12px' }}>
            Aucune inscription à confirmer : seules les sessions qui n&apos;ont pas encore commencé sont proposées.
          </Alert>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', mb: 1.5 }}>
              Inscriptions à confirmer
            </Typography>

            {options.map((row) => (
              <Box
                key={row.ls_id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  py: 1,
                  opacity: row.family ? 1 : 0.55
                }}
              >
                <Checkbox
                  checked={selected.includes(row.ls_id)}
                  disabled={!row.family}
                  onChange={() => toggle(row.ls_id)}
                  sx={{ p: 0.5 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>
                    {row.formation_name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    <Chip size="small" label={`Session N° ${row.number_session}`} sx={{ borderRadius: '8px' }} />
                    <Chip
                      size="small"
                      label={row.family_label || `${row.format} — format sans modèle`}
                      color={row.family ? 'primary' : 'default'}
                      variant="outlined"
                      sx={{ borderRadius: '8px' }}
                    />
                    <Typography variant="caption" color="textSecondary">
                      à partir du {row.start_date}
                    </Typography>
                    {row.confirmation_sent_at && (
                      <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 700 }}>
                        déjà confirmée le {row.confirmation_sent_at}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}

            {preview && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.secondary', mb: 1 }}>
                  Aperçu du mail — un seul message pour toutes les inscriptions cochées
                </Typography>

                {preview.map((mail) => (
                  <Box key={mail.family} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      {/* Chaque formation du mail garde ses propres champs ;
                          le modèle, lui, suit le format dominant. */}
                      {(mail.families || [mail.family_label]).map((label) => (
                        <Chip
                          key={label}
                          size="small"
                          color={label === mail.family_label ? 'primary' : 'default'}
                          variant={label === mail.family_label ? 'filled' : 'outlined'}
                          label={label}
                          sx={{ borderRadius: '8px' }}
                        />
                      ))}
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {mail.subject}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        à {mail.recipient} · modèle « {mail.template_name} »
                      </Typography>
                    </Box>
                    {/* Le mail est rendu tel quel : c'est l'envoi lui-même qu'on relit. */}
                    <Box
                      component="iframe"
                      title={`Aperçu ${mail.family_label}`}
                      srcDoc={mail.html}
                      sx={{
                        width: '100%',
                        height: 420,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '12px',
                        bgcolor: '#ffffff'
                      }}
                    />
                  </Box>
                ))}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" sx={{ fontWeight: 700 }}>
          Annuler
        </Button>
        <Button
          onClick={handlePreview}
          disabled={loading || selected.length === 0}
          sx={{ fontWeight: 700 }}
        >
          Aperçu
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          disabled={sending || !preview || selected.length === 0}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <MarkEmailReadIcon />}
          sx={{ borderRadius: '12px', fontWeight: 800 }}
        >
          Envoyer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CustomerInscriptionConfirmationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onSent: PropTypes.func
};

export default CustomerInscriptionConfirmationDialog;
